import {
  ELEMENT_TRACKING_ENABLED,
  ELEMENT_VIEW_DWELL_MS,
  ELEMENT_VIEW_RATIO,
  ELEMENT_VIEW_TRACKING_ENABLED,
} from "./config";
import {
  trackElementClicked,
  trackElementViewed,
  type ElementDescriptor,
} from "./element-tracking";

/**
 * One delegated listener and one IntersectionObserver for the whole app, both
 * driven by data attributes.
 *
 * The alternative — an `onClick` per component — does not survive contact with a
 * growing site: you never finish wiring it, and every new section ships
 * untracked. Here, instrumenting a section costs two HTML attributes:
 *
 *   <section data-track-section="vitrin_top">
 *     <div data-track-type="banner" data-track-id="42" data-track-slot="0"> … </div>
 *   </section>
 *
 * `data-track-type` marks the tracked element; `section` and `page` are read
 * from the nearest ancestor that declares them, so a row declares itself once.
 */

/** Marks a tracked element. Its value is the `element_type`. */
const TYPE_ATTR = "data-track-type";
/** Declared on the element or any ancestor — the block it belongs to. */
const SECTION_ATTR = "data-track-section";
/** Optional override; falls back to the current route's page_type. */
const PAGE_ATTR = "data-track-page";
/**
 * `data-track-view="off"` — count clicks on this element, skip its impression.
 * For surfaces too incidental to be a meaningful CTR denominator (a course
 * card's corner avatar), where an impression only inflates the volume of the
 * highest-volume event in the system without answering a question.
 */
const VIEW_ATTR = "data-track-view";

const TRACKED = `[${TYPE_ATTR}]`;

const num = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Assemble the descriptor for a tracked element. Returns `null` when the element
 * cannot say which section it is in — that is a wiring mistake, and an event
 * without a section answers no question anyway.
 */
const describe = (el: Element): ElementDescriptor | null => {
  const element_type = el.getAttribute(TYPE_ATTR);
  const section = el.closest(`[${SECTION_ATTR}]`)?.getAttribute(SECTION_ATTR);
  if (!element_type || !section) {
    if (import.meta.env.DEV) {
      console.warn(
        `[analytics] tracked element has no \`${SECTION_ATTR}\` ancestor — not counted.`,
        el,
      );
    }
    return null;
  }

  const courseId = num(el.getAttribute("data-track-course-id") ?? undefined);

  return {
    section,
    element_type,
    page_type: el.closest(`[${PAGE_ATTR}]`)?.getAttribute(PAGE_ATTR) ?? undefined,
    element_id: el.getAttribute("data-track-id") ?? undefined,
    slot_index: num(el.getAttribute("data-track-slot") ?? undefined),
    target_type: el.getAttribute("data-track-target") ?? undefined,
    // Indexed top-level columns — what turns "which section got clicks" into
    // "which section made money", and what makes the panel show the course
    // title instead of a bare id.
    course_id: Number.isInteger(courseId) ? courseId : undefined,
    mini_app_uuid: el.getAttribute("data-track-miniapp") ?? undefined,
  };
};

let started = false;

/** Elements already handed to the IntersectionObserver. */
const observed = new WeakSet<Element>();
/** Dwell timers — an element must hold its visibility, not just flash past. */
const dwellTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

let viewObserver: IntersectionObserver | null = null;

const clearDwell = (el: Element) => {
  const timer = dwellTimers.get(el);
  if (timer !== undefined) {
    clearTimeout(timer);
    dwellTimers.delete(el);
  }
};

/**
 * Half the element is on screen — or, for anything taller than the viewport,
 * half the screen is filled by it. Without the second half of that test an
 * element bigger than the window can never reach a 0.5 ratio and would report
 * no views at all.
 */
const isSeen = (entry: IntersectionObserverEntry): boolean => {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= ELEMENT_VIEW_RATIO) return true;

  const viewport = entry.rootBounds?.height ?? 0;
  return viewport > 0 && entry.intersectionRect.height >= viewport * ELEMENT_VIEW_RATIO;
};

const onIntersect: IntersectionObserverCallback = (entries) => {
  for (const entry of entries) {
    const el = entry.target;

    if (!isSeen(entry)) {
      clearDwell(el);
      continue;
    }
    if (dwellTimers.has(el)) continue;

    dwellTimers.set(
      el,
      setTimeout(() => {
        dwellTimers.delete(el);
        const descriptor = describe(el);
        // Deduped once per element per session inside trackElementViewed.
        if (descriptor) trackElementViewed(descriptor);
        // Seen once is seen; nothing more to learn from this element.
        viewObserver?.unobserve(el);
        observed.delete(el);
      }, ELEMENT_VIEW_DWELL_MS),
    );
  }
};

const observe = (el: Element) => {
  if (!viewObserver || observed.has(el)) return;
  if (el.getAttribute(VIEW_ATTR) === "off") return;
  observed.add(el);
  viewObserver.observe(el);
};

const unobserve = (el: Element) => {
  clearDwell(el);
  if (!observed.has(el)) return;
  observed.delete(el);
  viewObserver?.unobserve(el);
};

/** Every tracked element inside `root`, including `root` itself. */
const trackedWithin = (root: Node): Element[] => {
  if (!(root instanceof Element)) return [];
  const found = Array.from(root.querySelectorAll(TRACKED));
  if (root.matches(TRACKED)) found.unshift(root);
  return found;
};

/**
 * How far the pointer may travel between press and release and still count as a
 * tap. Above it the gesture was a drag — swiping a carousel, flicking a scroller
 * — and the browser still dispatches a `click` we must not record. Swiper
 * suppresses those for the app with a capture listener on its own root, which
 * runs *after* ours, so the guard has to live here.
 */
const TAP_SLOP_PX = 10;

let pressedAt: { x: number; y: number } | null = null;

const onPointerDown = (event: PointerEvent) => {
  pressedAt = { x: event.clientX, y: event.clientY };
};

const wasDrag = (event: MouseEvent): boolean => {
  if (!pressedAt) return false;
  const dx = event.clientX - pressedAt.x;
  const dy = event.clientY - pressedAt.y;
  pressedAt = null;
  return Math.hypot(dx, dy) > TAP_SLOP_PX;
};

const onClick = (event: MouseEvent) => {
  // Consumed first, and for every click: a gesture that ended outside any
  // tracked element must not leave its coordinates behind to be compared
  // against the next one.
  const dragged = wasDrag(event);

  const target = event.target;
  if (!(target instanceof Element)) return;

  const el = target.closest(TRACKED);
  if (!el || dragged) return;

  const descriptor = describe(el);
  if (descriptor) trackElementClicked(descriptor);
};

/**
 * Start click/view tracking. Idempotent — safe to call from a remounting
 * provider or React StrictMode's double effect.
 */
export const initElementTracking = (): void => {
  if (started || typeof window === "undefined" || typeof document === "undefined") return;
  if (!ELEMENT_TRACKING_ENABLED) return;
  started = true;

  // Capture phase: the click is recorded before the app's own handler runs, so
  // an element that navigates away — or one whose parent stops propagation —
  // still gets counted. `pointerdown` feeds the drag guard above.
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("click", onClick, true);

  if (!ELEMENT_VIEW_TRACKING_ENABLED || typeof IntersectionObserver === "undefined") return;

  viewObserver = new IntersectionObserver(onIntersect, {
    threshold: [0, ELEMENT_VIEW_RATIO],
  });

  // Content arrives asynchronously (queries resolve, carousels mount), so the
  // observer follows the DOM rather than scanning once at startup. Removals are
  // unobserved too: IntersectionObserver holds its targets strongly.
  const mutations = new MutationObserver((records) => {
    for (const record of records) {
      record.removedNodes.forEach((node) => trackedWithin(node).forEach(unobserve));
      record.addedNodes.forEach((node) => trackedWithin(node).forEach(observe));
    }
  });

  const start = () => {
    document.querySelectorAll(TRACKED).forEach(observe);
    mutations.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
};
